import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiAlertTriangle, FiClock, FiShield, FiTrendingUp, FiUsers } = FiIcons;

const MetricCard = ({ label, value, sub, icon, border, iconColor, valueColor, index }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.07 }}
    className={`bg-white rounded-xl border ${border} shadow-sm p-5 flex flex-col gap-3`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconColor}`}>
        <SafeIcon icon={icon} className="text-lg" />
      </div>
    </div>
    <div className={`text-3xl font-bold ${valueColor || 'text-slate-800'}`}>{value}</div>
    {sub && <p className="text-xs text-slate-400 font-medium">{sub}</p>}
  </motion.div>
);

const ComplianceMetrics = ({ records = [], learners = [] }) => {
  const getDeptCompliance = () => {
    const depts = [...new Set(learners.map(l => l?.department).filter(Boolean))];
    return depts.map(dept => {
      const deptLearners = learners.filter(l => l?.department === dept);
      const deptIds = deptLearners.map(l => String(l?.id));
      const deptRecords = records.filter(r => deptIds.includes(String(r?.learnerId)));
      const compliant = deptRecords.filter(r => r?.complianceStatus === 'Compliant').length;
      const pct = deptRecords.length ? Math.round((compliant / deptRecords.length) * 100) : 0;
      return { dept, pct, total: deptRecords.length, compliant };
    }).sort((a, b) => a.pct - b.pct);
  };

  const getUpcomingDueDates = () => {
    const today = new Date();
    const in14 = new Date(); in14.setDate(today.getDate() + 14);
    return records.filter(r => {
      if (!r?.dueDate) return false;
      const due = new Date(r.dueDate);
      return !r?.completionDate && due >= today && due <= in14;
    }).length;
  };

  const total = records.length;
  const compliant = records.filter(r => r?.complianceStatus === 'Compliant').length;
  const overdue = records.filter(r => r?.complianceStatus === 'Overdue').length;
  const atRisk = records.filter(r => r?.complianceStatus === 'At Risk').length;
  const notStarted = records.filter(r => r?.complianceStatus === 'Not Started').length;
  const upcoming = getUpcomingDueDates();
  const rate = total > 0 ? Math.round((compliant / total) * 100) : 0;
  const deptData = getDeptCompliance();
  const lowestDept = deptData[0];

  const cards = [
    { label: 'Overall Compliance Rate', value: `${rate}%`, sub: `${compliant} of ${total} records compliant`, icon: FiShield, border: 'border-blue-100', iconColor: 'bg-blue-50 text-blue-600', valueColor: rate >= 80 ? 'text-emerald-600' : rate >= 60 ? 'text-amber-600' : 'text-red-600' },
    { label: 'Compliant Learners', value: compliant, sub: 'Requirements met on time', icon: FiCheckCircle, border: 'border-emerald-100', iconColor: 'bg-emerald-50 text-emerald-600', valueColor: 'text-emerald-600' },
    { label: 'Overdue', value: overdue, sub: 'Past due date, not completed', icon: FiAlertTriangle, border: 'border-red-100', iconColor: 'bg-red-50 text-red-600', valueColor: 'text-red-600' },
    { label: 'At Risk / Not Started', value: atRisk + notStarted, sub: 'May miss upcoming deadline', icon: FiTrendingUp, border: 'border-orange-100', iconColor: 'bg-orange-50 text-orange-600', valueColor: 'text-orange-600' },
    { label: 'Due in 14 Days', value: upcoming, sub: 'Upcoming deadlines', icon: FiClock, border: 'border-violet-100', iconColor: 'bg-violet-50 text-violet-600' },
    { label: 'Lowest Dept.', value: `${lowestDept?.pct ?? 0}%`, sub: lowestDept?.dept ?? '—', icon: FiUsers, border: 'border-rose-100', iconColor: 'bg-rose-50 text-rose-600', valueColor: 'text-rose-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {cards.map((c, i) => <MetricCard key={c.label} {...c} index={i} />)}
    </div>
  );
};

export default ComplianceMetrics;